import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { isOffline as readOffline, subscribeConnectivity } from '../../offline/connectivity';
import { reconnect as probeReconnect } from '../../offline/offlineApi';
import { init as initSync } from '../../offline/syncEngine';
import { reloadParticipantGroups } from '../../hooks/useParticipantGroups';
import { reloadRoleSettings } from '../../hooks/useRoleSettings';
import { reloadWikis } from '../../hooks/useWikis';

// Offline-MODUS für Banner, deaktivierte Aktionen und Offline-Hinweise (siehe offline/connectivity.js):
// einmal offline -> bleibt offline bis App-Neustart/Reload oder erfolgreichem "Erneut verbinden".
//   onlineEpoch: zählt jedes erfolgreiche Wiederverbinden hoch — Seiten mit lokaler Kopie hängen es an
//                ihre Lade-Effekte und holen dann frische Daten (ohne Full-Reload, Eingaben bleiben).
const OfflineContext = createContext({
  isOffline: false,
  onlineEpoch: 0,
  reconnecting: false,
  reconnectFailed: false,
  reconnect: () => {},
});

export const useOffline = () => useContext(OfflineContext);

export const OfflineProvider = ({ children }) => {
  const [isOffline, setIsOffline] = useState(readOffline());
  const [onlineEpoch, setOnlineEpoch] = useState(0);
  const [reconnecting, setReconnecting] = useState(false);
  const [reconnectFailed, setReconnectFailed] = useState(false);
  const busy = useRef(false);

  useEffect(() => {
    initSync();
    return subscribeConnectivity((offline) => {
      setIsOffline(offline);
      if (!offline) {
        setReconnectFailed(false);
        setOnlineEpoch(e => e + 1);
        // Shared group list, role settings (dots, chips, price tables, role names) and wikis (home tiles,
        // wiki pages) — module state, not tied to a page.
        reloadParticipantGroups();
        reloadRoleSettings();
        reloadWikis();
      }
    });
  }, []);

  const reconnect = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    setReconnecting(true);
    setReconnectFailed(false);
    const ok = await probeReconnect();
    busy.current = false;
    setReconnecting(false);
    if (!ok) setReconnectFailed(true);
  }, []);

  return (
    <OfflineContext.Provider value={{ isOffline, onlineEpoch, reconnecting, reconnectFailed, reconnect }}>
      {children}
    </OfflineContext.Provider>
  );
};
